import type { SceneObject } from "../../SceneHost";
import { placeHtmlObject } from "../htmlLayerObject";
import {
  type CalculatorAction,
  type CalculatorKey,
  type CalculatorMode,
  type GraphPointSpec,
  type GraphSeriesSpec,
  MAX_GRAPH_POINTS,
  MAX_GRAPH_SERIES,
  calculatorTitle,
  convertResult,
  formatDisplayFE,
  splitCalcDisplay,
  type MemoryOp,
  newGraphPointId,
  newGraphSeriesId,
  readCalculatorState,
} from "../../../lib/board/calculatorLogic";
import { DEFAULT_GRAPH_RANGE, GRAPH_SERIES_COLORS, graphClientToWorld, paintGraph, panGraphRange, zoomGraphRangeAt } from "../../../lib/board/graphPlot";
import type { GraphRange } from "../../../lib/board/graphPlot";
import { getCategory, UNIT_CATEGORIES } from "../../../lib/board/unitConvert";
import { formatMathNumber } from "../../../lib/board/mathEval";


const GRAPH_SIDE_MIN_PX = 120;
const GRAPH_PLOT_MIN_PX = 220;
const GRAPH_SPLIT_DRAG_PX = 3;
/** Высота «строки» прокрутки в пикселях: deltaMode=1 приходит в строках, не в px. */
const WHEEL_LINE_PX = 16;
/**
 * Насколько сильно щипок меняет масштаб: множитель = exp(deltaY * k).
 * Тачпад шлёт много мелких событий, мышь — редкие крупные, поэтому шаг
 * пропорционален жесту, а не фиксирован.
 */
const GRAPH_ZOOM_PER_PX = 0.0035;
const GRAPH_ZOOM_STEP_MIN = 0.5;
const GRAPH_ZOOM_STEP_MAX = 2;

/** Дельты колеса в пикселях: браузер может прислать их в строках или страницах. */
function wheelDeltaPx(ev: WheelEvent, viewH: number): { dx: number; dy: number } {
  const k = ev.deltaMode === 1 ? WHEEL_LINE_PX : ev.deltaMode === 2 ? viewH : 1;
  return { dx: ev.deltaX * k, dy: ev.deltaY * k };
}
const graphSideWidths = new Map<string, number>();

function loadGraphSideWidth(objectId: string): number | null {
  const mem = graphSideWidths.get(objectId);
  if (mem != null && Number.isFinite(mem)) return mem;
  try {
    const raw = localStorage.getItem(`wb-graph-side:${objectId}`);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function saveGraphSideWidth(objectId: string, widthPx: number) {
  graphSideWidths.set(objectId, widthPx);
  try {
    localStorage.setItem(`wb-graph-side:${objectId}`, String(Math.round(widthPx)));
  } catch {
    /* quota / private mode */
  }
}

function readSideWidthPx(side: HTMLElement): number {
  return side.getBoundingClientRect().width;
}

function clampGraphSideWidth(paneWidth: number, splitterWidth: number, widthPx: number): number {
  const max = Math.max(GRAPH_SIDE_MIN_PX, paneWidth - splitterWidth - GRAPH_PLOT_MIN_PX);
  return Math.max(GRAPH_SIDE_MIN_PX, Math.min(max, widthPx));
}

function attachGraphSplit(
  pane: HTMLDivElement,
  side: HTMLDivElement,
  splitter: HTMLDivElement,
  plotWrap: HTMLDivElement,
  objectId: string,
  paneWidthHint: number,
  onResize: () => void
) {
  let dragging = false;
  let dragged = false;
  let pointerId: number | null = null;
  let grabOffset = 0;

  const measurePaneWidth = () =>
    pane.getBoundingClientRect().width || paneWidthHint || side.getBoundingClientRect().width + plotWrap.getBoundingClientRect().width;

  const applySideWidth = (widthPx: number) => {
    const paneWidth = measurePaneWidth();
    const splitterWidth = splitter.getBoundingClientRect().width || 7;
    const w = clampGraphSideWidth(paneWidth, splitterWidth, widthPx);
    pane.style.setProperty("--wb-graph-side-w", `${w}px`);
    onResize();
  };

  const saved = loadGraphSideWidth(objectId);
  if (saved != null && paneWidthHint > 0) {
    applySideWidth(saved);
    requestAnimationFrame(() => {
      if (!dragging && saved != null) applySideWidth(saved);
    });
  }

  splitter.addEventListener("pointerdown", (ev) => {
    if (ev.button !== 0) return;
    ev.preventDefault();
    ev.stopPropagation();
    dragging = true;
    dragged = false;
    pointerId = ev.pointerId;
    const paneLeft = pane.getBoundingClientRect().left;
    const sideWidth = readSideWidthPx(side);
    grabOffset = ev.clientX - paneLeft - sideWidth;
    pane.classList.add("is-resizing-graph");
    try {
      splitter.setPointerCapture(ev.pointerId);
    } catch {
      /* ignore */
    }
  });

  splitter.addEventListener("pointermove", (ev) => {
    if (!dragging || pointerId !== ev.pointerId) return;
    ev.preventDefault();
    ev.stopPropagation();
    if (!dragged && Math.abs(ev.movementX) + Math.abs(ev.movementY) < GRAPH_SPLIT_DRAG_PX) return;
    dragged = true;
    const paneLeft = pane.getBoundingClientRect().left;
    applySideWidth(ev.clientX - paneLeft - grabOffset);
  });

  const endDrag = (ev: PointerEvent) => {
    if (pointerId !== ev.pointerId) return;
    if (dragging) {
      dragging = false;
      pane.classList.remove("is-resizing-graph");
      if (dragged) {
        saveGraphSideWidth(objectId, readSideWidthPx(side));
      }
    }
    pointerId = null;
    try {
      splitter.releasePointerCapture(ev.pointerId);
    } catch {
      /* ignore */
    }
  };
  splitter.addEventListener("pointerup", endDrag);
  splitter.addEventListener("pointercancel", endDrag);

  stopProp(splitter);

  const ro = new ResizeObserver(() => {
    if (!dragging) onResize();
  });
  ro.observe(plotWrap);
}

export type CalculatorRenderOpts = {
  canEdit?: boolean;
  onCalculatorAction?: (objectId: string, action: CalculatorAction) => void;
  /** Выделить виджет (рамка / комментарии) — только по кнопке «Правка». */
  onRequestSelect?: (objectId: string) => void;
};

function stopProp(el: Element) {
  for (const type of ["pointerdown", "mousedown", "click", "dblclick"] as const) {
    el.addEventListener(type, (ev) => ev.stopPropagation());
  }
}

/** Блокируем всплытие с виджета, но не с canvas графика — там рисует перо. */
function stopPropExceptPlot(el: Element) {
  for (const type of ["pointerdown", "mousedown", "click", "dblclick"] as const) {
    el.addEventListener(type, (ev) => {
      if (ev.target instanceof Element && ev.target.closest(".wb-calc-card__plot")) return;
      ev.stopPropagation();
    });
  }
}

type KeyDef = {
  key: CalculatorKey | "";
  label: string;
  className?: string;
  spacer?: boolean;
};

/** Слот кнопки: при 2ⁿᵈ показывается secondary (как в Windows). */
type KeySlot = {
  primary: KeyDef;
  secondary?: KeyDef;
};

/**
 * Раскладка ближе к инженерному калькулятору Windows (5 колонок).
 * 2ⁿᵈ переключает альтернативы: x²↔x³, √↔∛, 10ˣ↔2ˣ, log↔ln-ряд, trig↔обратные.
 */
const WIN_SCI_ROWS: KeySlot[][] = [
  [
    { primary: { key: "2nd", label: "2ⁿᵈ", className: "wb-calc-card__btn--muted" } },
    { primary: { key: "pi", label: "π", className: "wb-calc-card__btn--fn" } },
    { primary: { key: "e", label: "e", className: "wb-calc-card__btn--fn" } },
    { primary: { key: "C", label: "C", className: "wb-calc-card__btn--muted" } },
    { primary: { key: "BKSP", label: "⌫", className: "wb-calc-card__btn--muted" } },
  ],
  [
    {
      primary: { key: "sqr", label: "x²", className: "wb-calc-card__btn--fn" },
      secondary: { key: "cube", label: "x³", className: "wb-calc-card__btn--fn" },
    },
    { primary: { key: "inv", label: "1/x", className: "wb-calc-card__btn--fn" } },
    { primary: { key: "abs", label: "|x|", className: "wb-calc-card__btn--fn" } },
    {
      primary: { key: "exp", label: "exp", className: "wb-calc-card__btn--fn" },
    },
    { primary: { key: "mod", label: "mod", className: "wb-calc-card__btn--fn" } },
  ],
  [
    {
      primary: { key: "sqrt", label: "²√x", className: "wb-calc-card__btn--fn" },
      secondary: { key: "cbrt", label: "³√x", className: "wb-calc-card__btn--fn" },
    },
    { primary: { key: "(", label: "(", className: "wb-calc-card__btn--fn" } },
    { primary: { key: ")", label: ")", className: "wb-calc-card__btn--fn" } },
    { primary: { key: "fact", label: "n!", className: "wb-calc-card__btn--fn" } },
    { primary: { key: "/", label: "÷", className: "wb-calc-card__btn--op" } },
  ],
  [
    { primary: { key: "^", label: "xʸ", className: "wb-calc-card__btn--fn" } },
    { primary: { key: "7", label: "7" } },
    { primary: { key: "8", label: "8" } },
    { primary: { key: "9", label: "9" } },
    { primary: { key: "*", label: "×", className: "wb-calc-card__btn--op" } },
  ],
  [
    {
      primary: { key: "pow10", label: "10ˣ", className: "wb-calc-card__btn--fn" },
      secondary: { key: "pow2", label: "2ˣ", className: "wb-calc-card__btn--fn" },
    },
    { primary: { key: "4", label: "4" } },
    { primary: { key: "5", label: "5" } },
    { primary: { key: "6", label: "6" } },
    { primary: { key: "-", label: "−", className: "wb-calc-card__btn--op" } },
  ],
  [
    { primary: { key: "log", label: "log", className: "wb-calc-card__btn--fn" } },
    { primary: { key: "1", label: "1" } },
    { primary: { key: "2", label: "2" } },
    { primary: { key: "3", label: "3" } },
    { primary: { key: "+", label: "+", className: "wb-calc-card__btn--op" } },
  ],
  [
    {
      primary: { key: "ln", label: "ln", className: "wb-calc-card__btn--fn" },
      secondary: { key: "exp", label: "eˣ", className: "wb-calc-card__btn--fn" },
    },
    { primary: { key: "±", label: "+/−", className: "wb-calc-card__btn--fn" } },
    { primary: { key: "0", label: "0" } },
    { primary: { key: ".", label: "," } },
    { primary: { key: "=", label: "=", className: "wb-calc-card__btn--eq" } },
  ],
];

/** Какое всплывающее меню открыто у калькулятора — состояние UI, не сцены. */
type CalcPanel = "trig" | "func" | "memory" | "history";
const openCalcPanel = new Map<string, CalcPanel | null>();
/** Включён ли hyp в меню «Тригонометрия» — тоже только у этого человека. */
const calcHypMode = new Map<string, boolean>();

/**
 * Меню «Тригонометрия» — как в Windows: два раздела по шесть функций,
 * 2ⁿᵈ внутри меню переключает на обратные.
 */
const TRIG_GROUPS: { title: string; slots: KeySlot[] }[] = [
  {
    title: "Тригонометрия",
    slots: [
      { primary: { key: "sin", label: "sin" }, secondary: { key: "asin", label: "sin⁻¹" } },
      { primary: { key: "cos", label: "cos" }, secondary: { key: "acos", label: "cos⁻¹" } },
      { primary: { key: "tan", label: "tan" }, secondary: { key: "atan", label: "tan⁻¹" } },
      { primary: { key: "sec", label: "sec" }, secondary: { key: "asec", label: "sec⁻¹" } },
      { primary: { key: "csc", label: "csc" }, secondary: { key: "acsc", label: "csc⁻¹" } },
      { primary: { key: "cot", label: "cot" }, secondary: { key: "acot", label: "cot⁻¹" } },
    ],
  },
  {
    title: "Гиперболические",
    slots: [
      { primary: { key: "sinh", label: "sinh" }, secondary: { key: "asinh", label: "sinh⁻¹" } },
      { primary: { key: "cosh", label: "cosh" }, secondary: { key: "acosh", label: "cosh⁻¹" } },
      { primary: { key: "tanh", label: "tanh" }, secondary: { key: "atanh", label: "tanh⁻¹" } },
      { primary: { key: "sech", label: "sech" }, secondary: { key: "asech", label: "sech⁻¹" } },
      { primary: { key: "csch", label: "csch" }, secondary: { key: "acsch", label: "csch⁻¹" } },
      { primary: { key: "coth", label: "coth" }, secondary: { key: "acoth", label: "coth⁻¹" } },
    ],
  },
];

/** Меню «Функция» — тот же набор, что в инженерном калькуляторе Windows. */
const FUNC_SLOTS: KeySlot[] = [
  { primary: { key: "floor", label: "⌊x⌋" } },
  { primary: { key: "ceil", label: "⌈x⌉" } },
  { primary: { key: "rand", label: "rand" } },
  { primary: { key: "dms", label: "dms" } },
  { primary: { key: "degs", label: "deg" } },
];

/** Строка памяти: MC MR M+ M− MS. */
const MEMORY_BUTTONS: { op: MemoryOp; label: string; title: string }[] = [
  { op: "clearAll", label: "MC", title: "Очистить память" },
  { op: "recall", label: "MR", title: "Взять число из памяти" },
  { op: "add", label: "M+", title: "Прибавить к памяти" },
  { op: "sub", label: "M−", title: "Вычесть из памяти" },
  { op: "store", label: "MS", title: "Записать число в память" },
];

function resolveSlot(slot: KeySlot, second: boolean): KeyDef {
  if (second && slot.secondary) return slot.secondary;
  return slot.primary;
}

function emit(
  opts: CalculatorRenderOpts,
  objectId: string,
  action: CalculatorAction,
  canEdit: boolean
) {
  if (!canEdit) return;
  opts.onCalculatorAction?.(objectId, action);
}

function makeBtn(
  def: KeyDef,
  canEdit: boolean,
  onClick: () => void
): HTMLButtonElement {
  const btn = document.createElement("button") as HTMLButtonElement;
  btn.type = "button";
  if (def.spacer) {
    btn.className = "wb-calc-card__btn wb-calc-card__btn--spacer";
    btn.disabled = true;
    btn.tabIndex = -1;
    btn.setAttribute("aria-hidden", "true");
    return btn;
  }
  btn.className = `wb-calc-card__btn${def.className ? ` ${def.className}` : ""}`;
  btn.textContent = def.label;
  btn.disabled = !canEdit;
  if (canEdit) {
    btn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      onClick();
    });
  }
  return btn;
}

function buildTabs(
  mode: CalculatorMode,
  enabledModes: CalculatorMode[],
  objectId: string,
  opts: CalculatorRenderOpts,
  canEdit: boolean
): HTMLDivElement | null {
  const items: { id: CalculatorMode; label: string }[] = [
    { id: "calc", label: "Счёт" },
    { id: "graph", label: "График" },
    { id: "convert", label: "Единицы" },
  ].filter((item) => enabledModes.includes(item.id));
  if (items.length <= 1) return null;

  const tabs = document.createElement("div") as HTMLDivElement;
  tabs.className = "wb-calc-card__tabs";
  for (const item of items) {
    const btn = document.createElement("button") as HTMLButtonElement;
    btn.type = "button";
    btn.className = `wb-calc-card__tab${mode === item.id ? " is-active" : ""}`;
    btn.textContent = item.label;
    btn.disabled = !canEdit;
    if (canEdit) {
      btn.addEventListener("click", (ev) => {
        ev.stopPropagation();
        emit(opts, objectId, { type: "setMode", mode: item.id }, canEdit);
      });
    }
    tabs.appendChild(btn);
  }
  return tabs;
}

/**
 * Запись выражения на экране — как её показывает Windows.
 *
 * Внутри выражение хранится в понятном парсеру виде (asin, sqrt, *), а на
 * экране человеку привычнее sin⁻¹, √ и ×. Длинные имена идут первыми,
 * иначе «asinh» превратился бы в «sin⁻¹h».
 */
const PRETTY_PARTS: [string, string][] = [
  ["asinh(", "sinh⁻¹("],
  ["acosh(", "cosh⁻¹("],
  ["atanh(", "tanh⁻¹("],
  ["acoth(", "coth⁻¹("],
  ["asech(", "sech⁻¹("],
  ["acsch(", "csch⁻¹("],
  ["asin(", "sin⁻¹("],
  ["acos(", "cos⁻¹("],
  ["atan(", "tan⁻¹("],
  ["acot(", "cot⁻¹("],
  ["asec(", "sec⁻¹("],
  ["acsc(", "csc⁻¹("],
  ["sqrt(", "√("],
  ["cbrt(", "∛("],
  ["degs(", "deg("],
  ["pi", "π"],
  ["mod", " mod "],
  ["*", "×"],
  ["/", "÷"],
  ["-", "−"],
];

function prettyCalcText(text: string): string {
  let out = text;
  for (const [from, to] of PRETTY_PARTS) out = out.split(from).join(to);
  return out;
}

function calcDiv(cls: string): HTMLDivElement {
  const el = document.createElement("div") as HTMLDivElement;
  el.className = cls;
  return el;
}

/** Маленькая кнопка: память, флаги, пункт меню. */
function chipBtn(
  label: string,
  title: string,
  enabled: boolean,
  onClick: () => void,
  extra = ""
): HTMLButtonElement {
  const b = document.createElement("button") as HTMLButtonElement;
  b.type = "button";
  b.className = extra ? `wb-calc-card__chip ${extra}` : "wb-calc-card__chip";
  b.textContent = label;
  b.title = title;
  b.disabled = !enabled;
  if (enabled) {
    b.addEventListener("click", (ev) => {
      ev.stopPropagation();
      onClick();
    });
  }
  return b;
}

type CalcPaneApi = {
  pane: HTMLDivElement;
  /** Открыть/закрыть всплывающее меню — зовётся и кнопкой журнала в шапке. */
  togglePanel: (panel: CalcPanel) => void;
};

/**
 * Вкладка «Счёт» повторяет инженерный калькулятор Windows: экран в две
 * строки, DEG и F-E, строка памяти, меню «Тригонометрия» и «Функция»,
 * клавиатура 5×7.
 */
function buildCalcPane(
  state: ReturnType<typeof readCalculatorState>,
  objectId: string,
  opts: CalculatorRenderOpts,
  canEdit: boolean
): CalcPaneApi {
  const pane = calcDiv("wb-calc-card__pane wb-calc-card__pane--calc");
  const second = state.secondMode;
  const press = (k: CalculatorKey) => emit(opts, objectId, { type: "key", key: k }, canEdit);
  const memOp = (op: MemoryOp, index = 0) =>
    emit(opts, objectId, { type: "memory", op, index }, canEdit);

  // --- экран: сверху ход вычисления, крупно — текущее число ---
  const displayWrap = calcDiv("wb-calc-card__display-wrap");
  const parts = splitCalcDisplay(state);
  const exprEl = calcDiv("wb-calc-card__expr");
  exprEl.textContent = prettyCalcText(parts.top) || " ";
  exprEl.title = parts.top;
  displayWrap.appendChild(exprEl);

  const display = calcDiv("wb-calc-card__display");
  display.textContent = prettyCalcText(formatDisplayFE(parts.main, state.fe)) || "0";
  display.title = parts.main;
  displayWrap.appendChild(display);
  pane.appendChild(displayWrap);

  // --- DEG / RAD и F-E ---
  const flags = calcDiv("wb-calc-card__flags");
  flags.appendChild(
    chipBtn(
      state.angleMode === "deg" ? "DEG" : "RAD",
      "Переключить градусы / радианы",
      canEdit,
      () => press(state.angleMode === "deg" ? "RAD" : "DEG"),
      "wb-calc-card__chip--flag"
    )
  );
  flags.appendChild(
    chipBtn(
      "F-E",
      "Показать результат в экспоненциальной записи",
      canEdit,
      () => press("FE"),
      `wb-calc-card__chip--flag${state.fe ? " is-active" : ""}`
    )
  );
  if (state.memory.length) {
    const m = document.createElement("span") as HTMLSpanElement;
    m.className = "wb-calc-card__mem-flag";
    m.textContent = "M";
    m.title = "В памяти есть число";
    flags.appendChild(m);
  }
  if (second) {
    const badge = document.createElement("span") as HTMLSpanElement;
    badge.className = "wb-calc-card__second-badge";
    badge.textContent = "2ⁿᵈ";
    flags.appendChild(badge);
  }
  pane.appendChild(flags);

  // --- строка памяти ---
  const hasMem = state.memory.length > 0;
  const memRow = calcDiv("wb-calc-card__memory");
  for (const def of MEMORY_BUTTONS) {
    const needsMem = def.op === "clearAll" || def.op === "recall";
    memRow.appendChild(
      chipBtn(def.label, def.title, canEdit && (!needsMem || hasMem), () => memOp(def.op))
    );
  }
  const memListBtn = chipBtn("M˅", "Показать содержимое памяти", hasMem, () =>
    togglePanel("memory")
  );
  memRow.appendChild(memListBtn);
  pane.appendChild(memRow);

  // --- меню «Тригонометрия» и «Функция» ---
  const menus = calcDiv("wb-calc-card__menus");
  const trigBtn = chipBtn(
    "Тригонометрия ⌄",
    "Тригонометрические функции",
    true,
    () => togglePanel("trig"),
    "wb-calc-card__chip--menu"
  );
  const funcBtn = chipBtn(
    "ƒ Функция ⌄",
    "Дополнительные функции",
    true,
    () => togglePanel("func"),
    "wb-calc-card__chip--menu"
  );
  menus.appendChild(trigBtn);
  menus.appendChild(funcBtn);
  pane.appendChild(menus);

  // --- клавиатура, а поверх неё всплывающие меню ---
  const padWrap = calcDiv("wb-calc-card__padwrap");
  const pad = calcDiv("wb-calc-card__pad wb-calc-card__pad--win");
  for (const row of WIN_SCI_ROWS) {
    const rowEl = calcDiv("wb-calc-card__row wb-calc-card__row--win");
    for (const slot of row) {
      const def = resolveSlot(slot, second);
      const isSecondKey = def.key === "2nd";
      const btn = makeBtn(
        {
          ...def,
          className: `${def.className ?? ""}${isSecondKey && second ? " is-active" : ""}${
            second && slot.secondary && def === slot.secondary ? " is-alt" : ""
          }`.trim(),
        },
        canEdit,
        () => {
          if (!def.key) return;
          press(def.key as CalculatorKey);
        }
      );
      if (isSecondKey) btn.title = "Вторые функции кнопок (2ⁿᵈ)";
      rowEl.appendChild(btn);
    }
    pad.appendChild(rowEl);
  }
  padWrap.appendChild(pad);

  const veil = calcDiv("wb-calc-card__flyout-veil");
  veil.addEventListener("click", (ev) => {
    ev.stopPropagation();
    setPanel(null);
  });
  padWrap.appendChild(veil);

  const panels = new Map<CalcPanel, HTMLDivElement>();

  function addPanel(id: CalcPanel, el: HTMLDivElement) {
    panels.set(id, el);
    padWrap.appendChild(el);
  }

  // --- меню «Тригонометрия» — один в один как в Windows: две строки
  // крупных клавиш «2ⁿᵈ sin cos tan / hyp sec csc cot». hyp меняет их
  // на гиперболические, 2ⁿᵈ — на обратные. Раньше здесь были два раздела
  // по шесть мелких кнопок с подписями — их было трудно попасть пальцем.
  const trigPanel = calcDiv("wb-calc-card__flyout wb-calc-card__flyout--keys");
  const trigGrid = calcDiv("wb-calc-card__flyout-keys wb-calc-card__flyout-keys--4");
  const fillTrig = () => {
    const hyp = calcHypMode.get(objectId) === true;
    const slots = TRIG_GROUPS[hyp ? 1 : 0]!.slots;
    trigGrid.replaceChildren();
    const toggle = (label: string, title: string, active: boolean, onClick: () => void) => {
      const b = makeBtn(
        { key: "", label, className: `wb-calc-card__btn--muted${active ? " is-active" : ""}` },
        canEdit,
        onClick
      );
      b.title = title;
      b.setAttribute("aria-pressed", String(active));
      return b;
    };
    const fn = (slot: KeySlot) => {
      const def = resolveSlot(slot, second);
      return makeBtn(
        { ...def, className: `wb-calc-card__btn--fn${second ? " is-alt" : ""}` },
        canEdit,
        () => {
          if (!def.key) return;
          setPanel(null);
          press(def.key as CalculatorKey);
        }
      );
    };
    trigGrid.appendChild(toggle("2ⁿᵈ", "Обратные функции", second, () => press("2nd")));
    for (const slot of slots.slice(0, 3)) trigGrid.appendChild(fn(slot));
    trigGrid.appendChild(
      toggle("hyp", "Гиперболические функции", hyp, () => {
        calcHypMode.set(objectId, !hyp);
        fillTrig();
      })
    );
    for (const slot of slots.slice(3)) trigGrid.appendChild(fn(slot));
  };
  fillTrig();
  trigPanel.appendChild(trigGrid);
  addPanel("trig", trigPanel);

  // --- меню «Функция» ---
  // Как в Windows: без заголовка, те же крупные клавиши в три столбца.
  const funcPanel = calcDiv("wb-calc-card__flyout wb-calc-card__flyout--keys");
  const funcGrid = calcDiv("wb-calc-card__flyout-keys wb-calc-card__flyout-keys--3");
  for (const slot of FUNC_SLOTS) {
    const def = slot.primary;
    funcGrid.appendChild(
      makeBtn({ ...def, className: "wb-calc-card__btn--fn" }, canEdit, () => {
        if (!def.key) return;
        setPanel(null);
        press(def.key as CalculatorKey);
      })
    );
  }
  funcPanel.appendChild(funcGrid);
  addPanel("func", funcPanel);

  // --- содержимое памяти ---
  const memPanel = calcDiv("wb-calc-card__flyout");
  const memHead = calcDiv("wb-calc-card__flyout-head");
  const memTitle = document.createElement("span") as HTMLSpanElement;
  memTitle.className = "wb-calc-card__flyout-title";
  memTitle.textContent = "Память";
  memHead.appendChild(memTitle);
  memHead.appendChild(
    chipBtn("Очистить всё", "Очистить память", canEdit && hasMem, () => {
      setPanel(null);
      memOp("clearAll");
    })
  );
  memPanel.appendChild(memHead);
  const memList = calcDiv("wb-calc-card__flyout-list");
  if (!hasMem) {
    const empty = calcDiv("wb-calc-card__flyout-empty");
    empty.textContent = "В памяти ничего нет";
    memList.appendChild(empty);
  }
  state.memory.forEach((value, index) => {
    const row = calcDiv("wb-calc-card__flyout-row");
    const valueBtn = document.createElement("button") as HTMLButtonElement;
    valueBtn.type = "button";
    valueBtn.className = "wb-calc-card__flyout-value";
    valueBtn.textContent = formatMathNumber(value);
    valueBtn.title = "Подставить это число";
    valueBtn.disabled = !canEdit;
    if (canEdit) {
      valueBtn.addEventListener("click", (ev) => {
        ev.stopPropagation();
        setPanel(null);
        memOp("recall", index);
      });
    }
    row.appendChild(valueBtn);
    const tools = calcDiv("wb-calc-card__flyout-tools");
    tools.appendChild(chipBtn("MC", "Убрать эту ячейку", canEdit, () => memOp("clearAt", index)));
    tools.appendChild(chipBtn("M+", "Прибавить к ячейке", canEdit, () => memOp("add", index)));
    tools.appendChild(chipBtn("M−", "Вычесть из ячейки", canEdit, () => memOp("sub", index)));
    row.appendChild(tools);
    memList.appendChild(row);
  });
  memPanel.appendChild(memList);
  addPanel("memory", memPanel);

  // --- журнал ---
  const histPanel = calcDiv("wb-calc-card__flyout");
  const histHead = calcDiv("wb-calc-card__flyout-head");
  const histTitle = document.createElement("span") as HTMLSpanElement;
  histTitle.className = "wb-calc-card__flyout-title";
  histTitle.textContent = "Журнал";
  histHead.appendChild(histTitle);
  histHead.appendChild(
    chipBtn("Очистить", "Очистить журнал", canEdit && state.history.length > 0, () => {
      setPanel(null);
      emit(opts, objectId, { type: "clearHistory" }, canEdit);
    })
  );
  histPanel.appendChild(histHead);
  const histList = calcDiv("wb-calc-card__flyout-list");
  if (!state.history.length) {
    const empty = calcDiv("wb-calc-card__flyout-empty");
    empty.textContent = "Здесь пока пусто";
    histList.appendChild(empty);
  }
  state.history.forEach((entry, index) => {
    const cut = entry.lastIndexOf("=");
    const btn = document.createElement("button") as HTMLButtonElement;
    btn.type = "button";
    btn.className = "wb-calc-card__flyout-hist";
    btn.disabled = !canEdit;
    btn.title = "Подставить результат";
    const top = document.createElement("span") as HTMLSpanElement;
    top.className = "wb-calc-card__flyout-hist-expr";
    top.textContent = cut > 0 ? `${entry.slice(0, cut)} =` : entry;
    const res = document.createElement("span") as HTMLSpanElement;
    res.className = "wb-calc-card__flyout-hist-res";
    res.textContent = cut > 0 ? entry.slice(cut + 1) : "";
    btn.appendChild(top);
    btn.appendChild(res);
    if (canEdit) {
      btn.addEventListener("click", (ev) => {
        ev.stopPropagation();
        setPanel(null);
        emit(opts, objectId, { type: "useHistory", index }, canEdit);
      });
    }
    histList.appendChild(btn);
  });
  histPanel.appendChild(histList);
  addPanel("history", histPanel);

  pane.appendChild(padWrap);

  function setPanel(panel: CalcPanel | null) {
    openCalcPanel.set(objectId, panel);
    for (const [id, el] of panels) {
      el.classList.toggle("is-open", id === panel);
    }
    veil.classList.toggle("is-open", panel !== null);
    trigBtn.classList.toggle("is-active", panel === "trig");
    funcBtn.classList.toggle("is-active", panel === "func");
    memListBtn.classList.toggle("is-active", panel === "memory");
  }

  function togglePanel(panel: CalcPanel) {
    setPanel(openCalcPanel.get(objectId) === panel ? null : panel);
  }

  // Меню переживает перерисовку объекта: иначе оно закрывалось бы от любой
  // нажатой кнопки, ведь объект после правки собирается заново.
  const restored = openCalcPanel.get(objectId) ?? null;
  setPanel(restored === "memory" && !hasMem ? null : restored);

  return { pane, togglePanel };
}

function parseCoord(raw: string): number {
  const n = Number(String(raw).trim().replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
}

function buildGraphPane(
  state: ReturnType<typeof readCalculatorState>,
  objectId: string,
  opts: CalculatorRenderOpts,
  canEdit: boolean,
  widgetWidth: number
): HTMLDivElement {
  const pane = document.createElement("div") as HTMLDivElement;
  pane.className = "wb-calc-card__pane wb-calc-card__pane--graph";
  const paneWidthHint = Math.max(280, widgetWidth - 20);

  const side = document.createElement("div") as HTMLDivElement;
  side.className = "wb-calc-card__graph-side";

  const plotWrap = document.createElement("div") as HTMLDivElement;
  plotWrap.className = "wb-calc-card__plot-wrap";
  const canvas = document.createElement("canvas") as HTMLCanvasElement;
  canvas.className = "wb-calc-card__plot";
  canvas.title = canEdit ? "Перетащите, чтобы сдвинуть · колёсико — масштаб" : "";
  plotWrap.appendChild(canvas);

  const coordReadout = document.createElement("div") as HTMLDivElement;
  coordReadout.className = "wb-calc-card__coord";
  coordReadout.hidden = true;
  plotWrap.appendChild(coordReadout);

  let liveSeries: GraphSeriesSpec[] = state.graphSeries.map((s) => ({ ...s }));
  let livePoints: GraphPointSpec[] = state.graphPoints.map((p) => ({ ...p }));
  let liveRange: GraphRange = { ...state.graphRange };
  let activeExpr: HTMLInputElement | null = null;
  let activeSeriesId: string | null = null;
  let rangeCommitTimer: number | null = null;

  const repaint = () => {
    paintGraph(canvas, liveSeries, liveRange, livePoints);
  };

  const commitSeries = () => {
    emit(opts, objectId, { type: "setGraphSeries", graphSeries: liveSeries }, canEdit);
  };
  const commitPoints = () => {
    emit(opts, objectId, { type: "setGraphPoints", graphPoints: livePoints }, canEdit);
  };
  const commitRange = () => {
    emit(opts, objectId, { type: "graphSetRange", graphRange: { ...liveRange } }, canEdit);
  };
  const scheduleCommitRange = () => {
    if (rangeCommitTimer != null) window.clearTimeout(rangeCommitTimer);
    rangeCommitTimer = window.setTimeout(() => {
      rangeCommitTimer = null;
      commitRange();
    }, 120);
  };

  const formatCoord = (n: number) => {
    const r = Math.round(n * 100) / 100;
    return String(r);
  };

  const zoomBar = document.createElement("div") as HTMLDivElement;
  zoomBar.className = "wb-calc-card__zoom";
  for (const z of [
    { label: "+", factor: 0.7, title: "Увеличить" },
    { label: "−", factor: 1.4, title: "Уменьшить" },
    { label: "⌂", reset: true, title: "Сбросить масштаб" },
  ] as const) {
    const b = document.createElement("button") as HTMLButtonElement;
    b.type = "button";
    b.className = "wb-calc-card__btn wb-calc-card__btn--muted";
    b.textContent = z.label;
    b.title = z.title;
    b.setAttribute("aria-label", z.title);
    b.disabled = !canEdit;
    if (canEdit) {
      b.addEventListener("click", (ev) => {
        ev.stopPropagation();
        if ("reset" in z && z.reset) {
          emit(opts, objectId, { type: "graphReset" }, canEdit);
          liveRange = { ...DEFAULT_GRAPH_RANGE };
          repaint();
        } else if ("factor" in z) {
          liveRange = zoomGraphRangeAt(
            liveRange,
            z.factor,
            (liveRange.xMin + liveRange.xMax) / 2,
            (liveRange.yMin + liveRange.yMax) / 2
          );
          repaint();
          commitRange();
        }
      });
    }
    zoomBar.appendChild(b);
  }
  plotWrap.appendChild(zoomBar);

  if (canEdit) {
    canvas.style.cursor = "grab";
    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    let pointerId: number | null = null;

    canvas.addEventListener("pointerdown", (ev) => {
      if (ev.button !== 0) return;
      ev.stopPropagation();
      ev.preventDefault();
      dragging = true;
      pointerId = ev.pointerId;
      lastX = ev.clientX;
      lastY = ev.clientY;
      canvas.style.cursor = "grabbing";
      try {
        canvas.setPointerCapture(ev.pointerId);
      } catch {
        /* ignore */
      }
    });
    canvas.addEventListener("pointermove", (ev) => {
      const rect = canvas.getBoundingClientRect();
      const cssX =
        ((ev.clientX - rect.left) / Math.max(1, rect.width)) * (canvas.clientWidth || rect.width);
      const cssY =
        ((ev.clientY - rect.top) / Math.max(1, rect.height)) * (canvas.clientHeight || rect.height);
      const world = graphClientToWorld(
        cssX,
        cssY,
        canvas.clientWidth || 1,
        canvas.clientHeight || 1,
        liveRange
      );
      if (Number.isFinite(world.x) && Number.isFinite(world.y)) {
        coordReadout.hidden = false;
        coordReadout.textContent = `(${formatCoord(world.x)}; ${formatCoord(world.y)})`;
      }

      if (!dragging || pointerId !== ev.pointerId) return;
      ev.stopPropagation();
      const dx = ev.clientX - lastX;
      const dy = ev.clientY - lastY;
      lastX = ev.clientX;
      lastY = ev.clientY;
      if (dx === 0 && dy === 0) return;
      liveRange = panGraphRange(
        liveRange,
        dx,
        dy,
        canvas.clientWidth || 1,
        canvas.clientHeight || 1
      );
      repaint();
    });
    const endDrag = (ev: PointerEvent) => {
      if (pointerId !== ev.pointerId) return;
      if (dragging) {
        dragging = false;
        canvas.style.cursor = "grab";
        commitRange();
      }
      pointerId = null;
      try {
        canvas.releasePointerCapture(ev.pointerId);
      } catch {
        /* ignore */
      }
    };
    canvas.addEventListener("pointerup", endDrag);
    canvas.addEventListener("pointercancel", endDrag);
    canvas.addEventListener("pointerleave", () => {
      if (!dragging) coordReadout.hidden = true;
    });

    // Раскладка жестов как в редакторах карт и досок:
    //   прокрутка (два пальца на тачпаде) — сдвиг области;
    //   щипок и Ctrl+колесо — масштаб под курсором.
    // Раньше любое колесо означало зум, поэтому попытка сдвинуть график
    // тачпадом меняла масштаб, а горизонтальный свайп (deltaY === 0)
    // приближал график: условие `deltaY > 0` уходило в ветку «приблизить».
    canvas.addEventListener(
      "wheel",
      (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        const viewW = canvas.clientWidth || 1;
        const viewH = canvas.clientHeight || 1;
        const { dx, dy } = wheelDeltaPx(ev, viewH);

        if (ev.ctrlKey || ev.metaKey) {
          if (dy === 0) return;
          const rect = canvas.getBoundingClientRect();
          const cssX = ((ev.clientX - rect.left) / Math.max(1, rect.width)) * viewW;
          const cssY = ((ev.clientY - rect.top) / Math.max(1, rect.height)) * viewH;
          const anchor = graphClientToWorld(cssX, cssY, viewW, viewH, liveRange);
          const factor = Math.min(
            GRAPH_ZOOM_STEP_MAX,
            Math.max(GRAPH_ZOOM_STEP_MIN, Math.exp(dy * GRAPH_ZOOM_PER_PX))
          );
          liveRange = zoomGraphRangeAt(liveRange, factor, anchor.x, anchor.y);
        } else {
          if (dx === 0 && dy === 0) return;
          // Знак обратный: прокрутка вниз уводит область обзора вниз,
          // то есть содержимое едет вверх — как при перетаскивании.
          liveRange = panGraphRange(liveRange, -dx, -dy, viewW, viewH);
        }
        repaint();
        scheduleCommitRange();
      },
      { passive: false }
    );
  }

  const fnTitle = document.createElement("div") as HTMLDivElement;
  fnTitle.className = "wb-calc-card__title wb-calc-card__title--graph";
  fnTitle.textContent = "Выражения";
  side.appendChild(fnTitle);

  const fnList = document.createElement("div") as HTMLDivElement;
  fnList.className = "wb-calc-card__fn-list";
  side.appendChild(fnList);

  function bindField(input: HTMLInputElement, onCommit: () => void) {
    stopProp(input);
    input.readOnly = !canEdit;
    if (!canEdit) return;
    input.addEventListener("keydown", (ev) => {
      ev.stopPropagation();
      if (ev.key === "Enter") {
        ev.preventDefault();
        input.blur();
      }
    });
    input.addEventListener("change", onCommit);
    input.addEventListener("blur", onCommit);
  }

  function renderFnRows() {
    fnList.replaceChildren();
    liveSeries.forEach((series, index) => {
      const row = document.createElement("div") as HTMLDivElement;
      row.className = `wb-calc-card__fn-row${activeSeriesId === series.id ? " is-active" : ""}${
        series.visible === false ? " is-hidden" : ""
      }`;
      row.style.setProperty("--fn-accent", series.color);

      const num = document.createElement("div") as HTMLDivElement;
      num.className = "wb-calc-card__fn-num";
      num.textContent = String(index + 1);
      row.appendChild(num);

      const colorWrap = document.createElement("div") as HTMLDivElement;
      colorWrap.className = "wb-calc-card__fn-color";

      const swatch = document.createElement("button") as HTMLButtonElement;
      swatch.type = "button";
      swatch.className = `wb-calc-card__fn-swatch${series.visible === false ? " is-off" : ""}`;
      swatch.style.setProperty("--swatch", series.color);
      swatch.title = series.visible === false ? "Показать график" : "Скрыть график (ПКМ — цвет)";
      swatch.setAttribute(
        "aria-label",
        series.visible === false ? "Показать график" : "Скрыть график"
      );
      swatch.disabled = !canEdit;

      const palette = document.createElement("div") as HTMLDivElement;
      palette.className = "wb-calc-card__fn-palette";
      palette.hidden = true;
      for (const c of GRAPH_SERIES_COLORS) {
        const opt = document.createElement("button") as HTMLButtonElement;
        opt.type = "button";
        opt.className = `wb-calc-card__fn-color-opt${c === series.color ? " is-active" : ""}`;
        opt.style.background = c;
        opt.title = c;
        opt.disabled = !canEdit;
        if (canEdit) {
          opt.addEventListener("click", (ev) => {
            ev.stopPropagation();
            liveSeries[index] = { ...liveSeries[index]!, color: c };
            palette.hidden = true;
            renderFnRows();
            repaint();
            commitSeries();
          });
        }
        palette.appendChild(opt);
      }
      if (canEdit) {
        swatch.addEventListener("click", (ev) => {
          ev.stopPropagation();
          liveSeries[index] = {
            ...liveSeries[index]!,
            visible: liveSeries[index]!.visible === false,
          };
          renderFnRows();
          repaint();
          commitSeries();
        });
        swatch.addEventListener("contextmenu", (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          const open = palette.hidden;
          fnList.querySelectorAll(".wb-calc-card__fn-palette").forEach((el) => {
            (el as HTMLElement).hidden = true;
          });
          palette.hidden = !open;
        });
      }
      colorWrap.appendChild(swatch);
      colorWrap.appendChild(palette);
      row.appendChild(colorWrap);

      const fields = document.createElement("div") as HTMLDivElement;
      fields.className = "wb-calc-card__fn-fields";

      const exprRow = document.createElement("div") as HTMLDivElement;
      exprRow.className = "wb-calc-card__fn-expr-row";
      const yLabel = document.createElement("span") as HTMLSpanElement;
      yLabel.className = "wb-calc-card__fn-prefix";
      yLabel.textContent = "y =";
      exprRow.appendChild(yLabel);

      const expr = document.createElement("input") as HTMLInputElement;
      expr.className = "wb-calc-card__input wb-calc-card__input--expr";
      expr.type = "text";
      expr.placeholder = "x^2";
      expr.value = series.expr;
      expr.title = "Функция y = f(x)";
      expr.addEventListener("focus", () => {
        activeExpr = expr;
        activeSeriesId = series.id;
        fnList.querySelectorAll(".wb-calc-card__fn-row").forEach((el) => {
          el.classList.toggle("is-active", el === row);
        });
      });
      expr.addEventListener("blur", () => {
        window.setTimeout(() => {
          if (activeExpr !== expr) return;
          activeExpr = null;
          activeSeriesId = null;
          row.classList.remove("is-active");
        }, 0);
      });
      expr.addEventListener("input", () => {
        liveSeries[index] = { ...liveSeries[index]!, expr: expr.value };
        repaint();
      });
      bindField(expr, () => {
        liveSeries[index] = { ...liveSeries[index]!, expr: expr.value };
        commitSeries();
      });
      exprRow.appendChild(expr);
      fields.appendChild(exprRow);

      const domainField = document.createElement("div") as HTMLDivElement;
      domainField.className = "wb-calc-card__field wb-calc-card__field--domain";

      const domainHead = document.createElement("div") as HTMLDivElement;
      domainHead.className = "wb-calc-card__field-head";

      const domainLabel = document.createElement("label") as HTMLLabelElement;
      domainLabel.className = "wb-calc-card__field-label";
      domainLabel.textContent = "ОДЗ";
      domainHead.appendChild(domainLabel);

      const domainHelp = document.createElement("span") as HTMLSpanElement;
      domainHelp.className = "wb-calc-card__odz-help";
      domainHelp.textContent = "?";
      domainHelp.tabIndex = 0;
      domainHelp.setAttribute("role", "button");
      domainHelp.setAttribute("aria-label", "Примеры записи ОДЗ");
      domainHelp.title =
        "[0; 2) или (−∞; 1]; (1; +∞) или x>=0 ; x!=1 или 0<=x<=5";
      domainHead.appendChild(domainHelp);
      domainField.appendChild(domainHead);

      const domain = document.createElement("input") as HTMLInputElement;
      domain.className = "wb-calc-card__input wb-calc-card__input--domain";
      domain.type = "text";
      domain.placeholder = "всё R";
      domain.value = series.domain;
      domain.addEventListener("input", () => {
        liveSeries[index] = { ...liveSeries[index]!, domain: domain.value };
        repaint();
      });
      bindField(domain, () => {
        liveSeries[index] = { ...liveSeries[index]!, domain: domain.value };
        commitSeries();
      });
      domainField.appendChild(domain);
      fields.appendChild(domainField);
      row.appendChild(fields);

      if (canEdit && liveSeries.length > 1) {
        const del = document.createElement("button") as HTMLButtonElement;
        del.type = "button";
        del.className = "wb-calc-card__mini-btn";
        del.textContent = "×";
        del.title = "Убрать функцию";
        del.addEventListener("click", (ev) => {
          ev.stopPropagation();
          liveSeries = liveSeries.filter((s) => s.id !== series.id);
          if (activeSeriesId === series.id) {
            activeSeriesId = null;
            activeExpr = null;
          }
          renderFnRows();
          repaint();
          commitSeries();
        });
        row.appendChild(del);
      }
      fnList.appendChild(row);
    });
  }
  renderFnRows();

  if (canEdit) {
    const addFn = document.createElement("button") as HTMLButtonElement;
    addFn.type = "button";
    addFn.className = "wb-calc-card__link-btn";
    addFn.textContent = "+ выражение";
    addFn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      if (liveSeries.length >= MAX_GRAPH_SERIES) return;
      const id = newGraphSeriesId();
      liveSeries = [
        ...liveSeries,
        {
          id,
          expr: "",
          domain: "",
          color: GRAPH_SERIES_COLORS[liveSeries.length % GRAPH_SERIES_COLORS.length]!,
          visible: true,
        },
      ];
      renderFnRows();
      commitSeries();
      const inputs = fnList.querySelectorAll(".wb-calc-card__input--expr");
      const input = inputs[liveSeries.length - 1] as HTMLInputElement | undefined;
      input?.focus();
    });
    side.appendChild(addFn);
  }

  const ptDivider = document.createElement("div") as HTMLDivElement;
  ptDivider.className = "wb-calc-card__section-rule";
  side.appendChild(ptDivider);

  const ptTitle = document.createElement("div") as HTMLDivElement;
  ptTitle.className = "wb-calc-card__title wb-calc-card__title--graph";
  ptTitle.textContent = "Точки";
  side.appendChild(ptTitle);

  const ptList = document.createElement("div") as HTMLDivElement;
  ptList.className = "wb-calc-card__pt-list";
  side.appendChild(ptList);

  function renderPtRows() {
    ptList.replaceChildren();
    livePoints.forEach((pt, index) => {
      const row = document.createElement("div") as HTMLDivElement;
      row.className = "wb-calc-card__pt-row";

      const kinds = document.createElement("div") as HTMLDivElement;
      kinds.className = "wb-calc-card__pt-kinds";

      const hollow = document.createElement("button") as HTMLButtonElement;
      hollow.type = "button";
      hollow.className = `wb-calc-card__pt-kind${!pt.filled ? " is-active" : ""}`;
      hollow.textContent = "○";
      hollow.title = "Выколотая точка";
      hollow.setAttribute("aria-label", "Выколотая точка");
      hollow.disabled = !canEdit;

      const filled = document.createElement("button") as HTMLButtonElement;
      filled.type = "button";
      filled.className = `wb-calc-card__pt-kind is-filled-opt${pt.filled ? " is-active" : ""}`;
      filled.textContent = "●";
      filled.title = "Полная точка";
      filled.setAttribute("aria-label", "Полная точка");
      filled.disabled = !canEdit;

      if (canEdit) {
        hollow.addEventListener("click", (ev) => {
          ev.stopPropagation();
          livePoints[index] = { ...livePoints[index]!, filled: false };
          renderPtRows();
          repaint();
          commitPoints();
        });
        filled.addEventListener("click", (ev) => {
          ev.stopPropagation();
          livePoints[index] = { ...livePoints[index]!, filled: true };
          renderPtRows();
          repaint();
          commitPoints();
        });
      }
      kinds.appendChild(hollow);
      kinds.appendChild(filled);
      row.appendChild(kinds);

      const xIn = document.createElement("input") as HTMLInputElement;
      xIn.className = "wb-calc-card__input wb-calc-card__input--pt";
      xIn.type = "text";
      xIn.placeholder = "x";
      xIn.value = String(pt.x);
      xIn.title = "x";
      const yIn = document.createElement("input") as HTMLInputElement;
      yIn.className = "wb-calc-card__input wb-calc-card__input--pt";
      yIn.type = "text";
      yIn.placeholder = "y";
      yIn.value = String(pt.y);
      yIn.title = "y";

      const commitPtCoords = () => {
        const x = Number(String(xIn.value).replace(",", "."));
        const y = Number(String(yIn.value).replace(",", "."));
        if (!Number.isFinite(x) || !Number.isFinite(y)) return;
        livePoints[index] = { ...livePoints[index]!, x, y };
        repaint();
        commitPoints();
      };
      xIn.addEventListener("input", () => {
        const x = Number(String(xIn.value).replace(",", "."));
        const y = Number(String(yIn.value).replace(",", "."));
        if (!Number.isFinite(x) || !Number.isFinite(y)) return;
        livePoints[index] = { ...livePoints[index]!, x, y };
        repaint();
      });
      yIn.addEventListener("input", () => {
        const x = Number(String(xIn.value).replace(",", "."));
        const y = Number(String(yIn.value).replace(",", "."));
        if (!Number.isFinite(x) || !Number.isFinite(y)) return;
        livePoints[index] = { ...livePoints[index]!, x, y };
        repaint();
      });
      bindField(xIn, commitPtCoords);
      bindField(yIn, commitPtCoords);
      row.appendChild(xIn);
      row.appendChild(yIn);

      if (canEdit) {
        const del = document.createElement("button") as HTMLButtonElement;
        del.type = "button";
        del.className = "wb-calc-card__mini-btn";
        del.textContent = "×";
        del.title = "Убрать точку";
        del.addEventListener("click", (ev) => {
          ev.stopPropagation();
          livePoints = livePoints.filter((p) => p.id !== pt.id);
          renderPtRows();
          repaint();
          commitPoints();
        });
        row.appendChild(del);
      }
      ptList.appendChild(row);
    });
  }
  renderPtRows();

  if (canEdit) {
    const addPt = document.createElement("button") as HTMLButtonElement;
    addPt.type = "button";
    addPt.className = "wb-calc-card__link-btn";
    addPt.textContent = "+ точка";
    addPt.addEventListener("click", (ev) => {
      ev.stopPropagation();
      if (livePoints.length >= MAX_GRAPH_POINTS) return;
      livePoints = [...livePoints, { id: newGraphPointId(), x: 0, y: 0, filled: true }];
      renderPtRows();
      commitPoints();
      repaint();
    });
    side.appendChild(addPt);
  }

  const chips = document.createElement("div") as HTMLDivElement;
  chips.className = "wb-calc-card__chips wb-calc-card__chips--graph";
  const chipsLabel = document.createElement("div") as HTMLDivElement;
  chipsLabel.className =
    "wb-calc-card__title wb-calc-card__title--graph wb-calc-card__chips-label";
  chipsLabel.textContent = "Вставить";
  side.appendChild(chipsLabel);
  for (const chip of [
    { label: "x", insert: "x" },
    { label: "x²", insert: "x^2" },
    { label: "√", insert: "sqrt(" },
    { label: "|x|", insert: "abs(" },
    { label: "1/x", insert: "1/x" },
    { label: "sin", insert: "sin(" },
    { label: "cos", insert: "cos(" },
    { label: "tan", insert: "tan(" },
    { label: "ln", insert: "ln(" },
    { label: "log", insert: "log(" },
    { label: "eˣ", insert: "exp(" },
    { label: "π", insert: "pi" },
  ] as const) {
    const b = document.createElement("button") as HTMLButtonElement;
    b.type = "button";
    b.className = "wb-calc-card__chip";
    b.textContent = chip.label;
    b.disabled = !canEdit;
    if (canEdit) {
      b.addEventListener("pointerdown", (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        const target =
          activeExpr ??
          (fnList.querySelector(".wb-calc-card__input--expr") as HTMLInputElement | null);
        if (!target) return;
        target.value = `${target.value}${chip.insert}`.slice(0, 120);
        const idx = [...fnList.querySelectorAll(".wb-calc-card__input--expr")].indexOf(target);
        if (idx >= 0 && liveSeries[idx]) {
          liveSeries[idx] = { ...liveSeries[idx]!, expr: target.value };
          repaint();
          commitSeries();
        }
      });
    }
    chips.appendChild(b);
  }
  side.appendChild(chips);

  const splitter = document.createElement("div") as HTMLDivElement;
  splitter.className = "wb-calc-card__graph-split";
  splitter.title = "Потяните, чтобы изменить ширину панели";
  splitter.setAttribute("role", "separator");
  splitter.setAttribute("aria-orientation", "vertical");
  splitter.setAttribute("aria-label", "Ширина панели выражений");

  // Как в Desmos: выражения слева, график справа
  pane.appendChild(side);
  pane.appendChild(splitter);
  pane.appendChild(plotWrap);

  attachGraphSplit(pane, side, splitter, plotWrap, objectId, paneWidthHint, repaint);

  requestAnimationFrame(() => {
    repaint();
  });

  return pane;
}

function buildConvertPane(
  state: ReturnType<typeof readCalculatorState>,
  objectId: string,
  opts: CalculatorRenderOpts,
  canEdit: boolean
): HTMLDivElement {
  const pane = document.createElement("div") as HTMLDivElement;
  pane.className = "wb-calc-card__pane wb-calc-card__pane--convert";

  const list = document.createElement("div") as HTMLDivElement;
  list.className = "wb-calc-card__cat-list";
  for (const cat of UNIT_CATEGORIES) {
    const btn = document.createElement("button") as HTMLButtonElement;
    btn.type = "button";
    btn.className = `wb-calc-card__cat${
      state.convertCategory === cat.id ? " is-active" : ""
    }`;
    btn.textContent = cat.label;
    btn.disabled = !canEdit;
    if (canEdit) {
      btn.addEventListener("click", (ev) => {
        ev.stopPropagation();
        emit(opts, objectId, { type: "setConvertCategory", category: cat.id }, canEdit);
      });
    }
    list.appendChild(btn);
  }
  pane.appendChild(list);

  const form = document.createElement("div") as HTMLDivElement;
  form.className = "wb-calc-card__convert-form";

  const cat = getCategory(state.convertCategory) ?? UNIT_CATEGORIES[0]!;

  const fromRow = document.createElement("div") as HTMLDivElement;
  fromRow.className = "wb-calc-card__convert-row";
  const fromInput = document.createElement("input") as HTMLInputElement;
  fromInput.className = "wb-calc-card__input";
  fromInput.type = "text";
  fromInput.inputMode = "decimal";
  fromInput.value = state.convertValue;
  fromInput.readOnly = !canEdit;
  stopProp(fromInput);

  const fromSel = document.createElement("select") as HTMLSelectElement;
  fromSel.className = "wb-calc-card__select";
  fromSel.disabled = !canEdit;
  stopProp(fromSel);
  for (const u of cat.units) {
    const opt = document.createElement("option") as HTMLOptionElement;
    opt.value = u.id;
    opt.textContent = u.label;
    if (u.id === state.convertFrom) opt.selected = true;
    fromSel.appendChild(opt);
  }
  fromRow.appendChild(fromInput);
  fromRow.appendChild(fromSel);
  form.appendChild(fromRow);

  const swap = document.createElement("button") as HTMLButtonElement;
  swap.type = "button";
  swap.className = "wb-calc-card__btn wb-calc-card__btn--muted wb-calc-card__swap";
  swap.textContent = "⇅";
  swap.disabled = !canEdit;
  if (canEdit) {
    swap.addEventListener("click", (ev) => {
      ev.stopPropagation();
      emit(opts, objectId, { type: "swapConvert" }, canEdit);
    });
  }
  form.appendChild(swap);

  const toRow = document.createElement("div") as HTMLDivElement;
  toRow.className = "wb-calc-card__convert-row";
  const toOut = document.createElement("div") as HTMLDivElement;
  toOut.className = "wb-calc-card__display wb-calc-card__display--convert";

  const toSel = document.createElement("select") as HTMLSelectElement;
  toSel.className = "wb-calc-card__select";
  toSel.disabled = !canEdit;
  stopProp(toSel);
  for (const u of cat.units) {
    const opt = document.createElement("option") as HTMLOptionElement;
    opt.value = u.id;
    opt.textContent = u.label;
    if (u.id === state.convertTo) opt.selected = true;
    toSel.appendChild(opt);
  }

  const refreshOut = () => {
    const live = {
      ...state,
      convertFrom: fromSel.value,
      convertTo: toSel.value,
      convertValue: fromInput.value,
    };
    toOut.textContent = convertResult(live, fromInput.value);
  };
  refreshOut();

  if (canEdit) {
    fromInput.addEventListener("input", () => {
      refreshOut();
    });
    fromInput.addEventListener("change", () => {
      if (fromInput.value === state.convertValue) return;
      emit(opts, objectId, { type: "setConvertValue", value: fromInput.value }, canEdit);
    });
    fromInput.addEventListener("blur", () => {
      if (fromInput.value === state.convertValue) return;
      emit(opts, objectId, { type: "setConvertValue", value: fromInput.value }, canEdit);
    });
    fromSel.addEventListener("change", () => {
      refreshOut();
      emit(opts, objectId, { type: "setConvertFrom", unit: fromSel.value }, canEdit);
    });
    toSel.addEventListener("change", () => {
      refreshOut();
      emit(opts, objectId, { type: "setConvertTo", unit: toSel.value }, canEdit);
    });
  }

  toRow.appendChild(toOut);
  toRow.appendChild(toSel);
  form.appendChild(toRow);

  pane.appendChild(form);
  return pane;
}

export function renderCalculatorObject(
  obj: SceneObject,
  opts: CalculatorRenderOpts = {}
): HTMLElement {
  const canEdit = Boolean(opts.canEdit);
  const w = typeof obj.w === "number" ? obj.w : 400;
  const h = typeof obj.h === "number" ? obj.h : 480;
  const state = readCalculatorState(obj);

  const g = document.createElement("div");
  g.setAttribute("class", "wb-scene-obj wb-scene-obj--calculator");
  g.setAttribute("data-object-id", obj.id);
  placeHtmlObject(g, obj, w, h);

  const card = document.createElement("div");
  card.style.width = "100%";
  card.style.height = "100%";
  card.style.boxSizing = "border-box";
  card.setAttribute("class", `wb-calc-card wb-calc-card--${state.mode}`);
  card.setAttribute("data-object-id", obj.id);
  stopPropExceptPlot(card);

  const head = document.createElement("div");
  head.setAttribute("class", "wb-calc-card__head");

  const title = document.createElement("div");
  title.setAttribute("class", "wb-calc-card__title");
  title.textContent = calculatorTitle(state.enabledModes);
  head.appendChild(title);

  let journalBtn: HTMLButtonElement | null = null;
  if (state.mode === "calc") {
    journalBtn = document.createElement("button") as HTMLButtonElement;
    journalBtn.type = "button";
    journalBtn.className = "wb-calc-card__journal";
    journalBtn.textContent = "🕘";
    journalBtn.title = "Журнал вычислений";
    journalBtn.setAttribute("aria-label", "Журнал вычислений");
    head.appendChild(journalBtn);
  }

  const editBtn = document.createElement("button") as HTMLButtonElement;
  editBtn.type = "button";
  editBtn.setAttribute("class", "wb-calc-card__edit");
  editBtn.setAttribute("data-object-id", obj.id);
  editBtn.title = "Выделить для перемещения, размера, комментария";
  editBtn.textContent = "Передвинуть";
  if (canEdit && opts.onRequestSelect) {
    const selectSelf = (ev: Event) => {
      ev.preventDefault();
      ev.stopPropagation();
      opts.onRequestSelect?.(obj.id);
    };
    editBtn.addEventListener("pointerdown", selectSelf);
    editBtn.addEventListener("click", selectSelf);
  } else {
    editBtn.disabled = true;
  }
  head.appendChild(editBtn);
  card.appendChild(head);
  const tabs = buildTabs(state.mode, state.enabledModes, obj.id, opts, canEdit);
  if (tabs) card.appendChild(tabs);

  if (state.mode === "graph") {
    card.appendChild(buildGraphPane(state, obj.id, opts, canEdit, w));
  } else if (state.mode === "convert") {
    card.appendChild(buildConvertPane(state, obj.id, opts, canEdit));
  } else {
    const calc = buildCalcPane(state, obj.id, opts, canEdit);
    card.appendChild(calc.pane);
    journalBtn?.addEventListener("click", (ev) => {
      ev.stopPropagation();
      calc.togglePanel("history");
    });
  }

  g.appendChild(card);
  return g;
}
